import merge from "lodash.merge";
import { render } from "react-dom";
import { Provider } from "react-redux";

import { store } from "./store/store";
import { WebchatWidget } from "./components/WebchatWidget";
import { sessionDataHandler } from "./sessionDataHandler";
import { initConfig } from "./store/actions/initActions";
import { ConfigState } from "./store/definitions";
import { initLogger } from "./logger";
import type { CobrowseVisitor, PresenceVisitor } from "./types/Glance";

const defaultConfig: Partial<ConfigState> = {
    serverUrl: "http://localhost:3001",
    theme: {
        isLight: true
    },
    fileAttachment: {
        enabled: true,
        maxFileSize: 16777216, // 16 MB
        acceptedExtensions: ["jpg", "jpeg", "png", "amr", "mp3", "mp4", "pdf", "txt"]
    },
    transcript: {
        downloadEnabled: false,
        emailEnabled: false,
        emailSubject: (agentNames) => {
            let subject = "Transcript of your chat";
            if (agentNames.length > 0) {
                subject = subject.concat(` with ${agentNames[0]}`);
                agentNames.slice(1).forEach((name) => (subject = subject.concat(` and ${name}`)));
            }
            return subject;
        },
        emailContent: (customerName, transcript) => {
            return `<div><h1 style="text-align:center;">Chat Transcript</h1><p>Hello ${customerName},<br><br>Please see below your transcript, with any associated files attached, as requested.<br><br>${transcript}</p></div>`;
        }
    }
};

const getCobrowseConfig = (config: ConfigState) => {
    const glanceCobrowserScript = document.getElementById("glance-cobrowse");

    if (glanceCobrowserScript === null) {
        return config;
    }

    const { site, groupid: strGroupId } = glanceCobrowserScript.dataset;

    const parsedGroupId = strGroupId === undefined ? undefined : parseInt(strGroupId, 10);

    const newConfig = { ...config };

    if (parsedGroupId !== undefined && !isNaN(parsedGroupId) && site !== undefined) {
        newConfig.glance = {
            groupid: parsedGroupId,
            site
        };
    }

    return newConfig;
};

const validateAutoInitConfig = (config: ConfigState) => {
    const { autoInitConfig } = config;

    if (!autoInitConfig) {
        return;
    }

    if (!autoInitConfig.email) {
        throw new Error(`email is required on the autoInitConfig`);
    }
    if (!autoInitConfig.name) {
        throw new Error(`name is required on the autoInitConfig`);
    }
    if (!autoInitConfig.query) {
        throw new Error(`query is required on the autoInitConfig`);
    }
};

const initWebchat = async (config: ConfigState) => {
    const mergedConfig = getCobrowseConfig(merge(defaultConfig, config));

    validateAutoInitConfig(mergedConfig);

    sessionDataHandler.setEndpoint(mergedConfig.serverUrl);
    sessionDataHandler.setApiKey(mergedConfig.apiKey);
    store.dispatch(initConfig(mergedConfig));
    initLogger();

    let webChatRoot: HTMLElement | null;

    // Find the div if doesn't exist
    if ((webChatRoot = document.getElementById("iqvia-webchat-widget-root")) === null) {
        webChatRoot = document.createElement("div");
        webChatRoot.id = "iqvia-webchat-widget-root";
    }

    document.body.appendChild(webChatRoot);

    render(
        <Provider store={store}>
            <WebchatWidget />
        </Provider>,
        webChatRoot
    );

    if (window.Cypress) {
        window.store = store;
    }
};

declare global {
    interface Window {
        Twilio: {
            initWebchat: (config: ConfigState) => void;
        };
        Cypress: Cypress.Cypress;
        store: typeof store;
        GLANCE: {
            Cobrowse: {
                Visitor: CobrowseVisitor;
            };
            VERSION: string;
            PATCH: string;
            Presence: {
                Visitor: PresenceVisitor;
            };
        };
    }
}

// Expose `initWebchat` function to window object
Object.assign(window, {
    Twilio: {
        initWebchat
    }
});
